import classnames from "classnames";
import * as React from "react";
import * as _ from "lodash";
import {
    useTable,
    useSortBy,
    useExpanded,
    useFilters,
    useResizeColumns,
    usePagination,
    useColumnOrder,
} from "react-table";

import { SORT_DOWN, SORT_UP, CHEVRON_UP } from "../../../Constants";
import { OutotecIcons } from "../../General/Icons/";
import "../../.././Assets/scss/index.scss";
import { useMemo, useState, useEffect } from "react";
import Select, { components } from "react-select";
import * as Const from "../../../Constants";

export const DataGridReusable: React.FC<any> = ({
    data,
    columns,
    isSortable,
    showPagination,
    filterable,
    defaultPageSize,
    hideHeader = false,
    columnsReorderingEnabled = false,
    SubComponent,
    fetchData,
    pages,
    manual = false,
    setTableState,
    routeChanged,
    loading,
    fromSearchDataGrid = false,
}) => {
    const [columns1, setColumns1] = useState(columns);

    useEffect(() => {
        setColumns1(columns);
    }, [columns]);

    // Sort columns based on the `order` property
    const sortedColumns = useMemo(
        () => [...columns1].sort((a, b) => a.order - b.order),
        [columns1]
    );

    const lastFixedColumnIndex = columns.filter(
        (col) => col.fixed === "left"
    ).length; // Filter left-fixed columns

    // Function to move column left
    const moveColumnLeft = (index) => {
        if (index > 0) {
            const newColumns = [...columns1];
            const temp = newColumns[index];
            newColumns[index] = newColumns[index - 1];
            newColumns[index - 1] = temp;

            // Update the order properties
            newColumns[index].order = newColumns[index - 1].order;
            newColumns[index - 1].order = temp.order;

            setColumns1(newColumns); // Update state to trigger re-render
        }
    };

    // Function to move column right
    const moveColumnRight = (index) => {
        if (index < columns.length - 1) {
            const newColumns = [...columns1];
            const temp = newColumns[index];
            newColumns[index] = newColumns[index + 1];
            newColumns[index + 1] = temp;

            // Update the order properties
            newColumns[index].order = newColumns[index + 1].order;
            newColumns[index + 1].order = temp.order;

            setColumns1(newColumns); // Update state to trigger re-render
        }
    };

    const CustomDropdownIndicatorInPagination = (props) => {
        return (
            <components.DropdownIndicator {...props}>
                <OutotecIcons type={Const.SORT_DOWN} />
            </components.DropdownIndicator>
        );
    };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        // rows,
        page, // using page instead of rows to apply pagination
        prepareRow,
        visibleColumns,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state,
    } = useTable(
        {
            columns: sortedColumns,
            data,
            initialState: {
                pageIndex: 0, // Start at the first page
                pageSize: defaultPageSize ? defaultPageSize : 150000, // Default number of rows per page
            },
            manualPagination: manual,
            manualFilters: manual,
            manualSortBy: manual,
        },
        useFilters,
        useSortBy,
        useExpanded,
        useResizeColumns,
        usePagination,
        useColumnOrder
    );

    const { pageIndex, pageSize } = state;
    const [inputPage, setInputPage] = useState(pageIndex + 1);
    const [pageNumberInManualPagination, setPageNumberInManualPagination] =
        useState(1);

    useEffect(() => {
        setInputPage(pageIndex + 1); // Update the inputPage when pageIndex changes
    }, [pageIndex, pageSize, routeChanged]);

    // Trigger fetchData function when state changes
    useEffect(() => {
        if (fetchData && manual) fetchData(state, inputPage - 1);
    }, [state.sortBy, pageSize]);

    // Synchronize the table state with the parent(PagedDataGrid only since it implements server side pagination,sorting and filtering) whenever it changes
    useEffect(() => {
        if (setTableState)
            setTableState({
                state,
            });
    }, [inputPage, pageSize, state]);

    const totalColumnWidth = visibleColumns.reduce((totalWidth, column) => {
        // Sum up the width of each column
        return totalWidth + (column.show !== false ? column.width || 0 : 0);
    }, 0);

    const numberOfRowsOptions = [
        { value: 5, label: "5 rows" },
        { value: 10, label: "10 rows" },
        { value: 20, label: "20 rows" },
        { value: 25, label: "25 rows" },
        { value: 50, label: "50 rows" },
        { value: 100, label: "100 rows" },
    ];

    // Handle the page number input and update on Enter key press
    const handlePageInput = (event) => {
        const value = event.target.value;
        // Allow only numbers or an empty string (for clearing input)
        if (/^[0-9\b]*$/.test(value)) {
            if (manual) {
                value >= pages ? setInputPage(pages) : setInputPage(value);
            } else {
                value >= pageCount
                    ? setInputPage(pageCount)
                    : setInputPage(value);
            }
        }
    };

    // Handle the key press to apply the page change when Enter is pressed
    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            const pageNumber = event.target.value;
            if (manual) {
                const inputField = event.target as HTMLTextAreaElement;
                inputField.blur(); // to call the api in onblur function below
            } else {
                if (pageNumber === "") {
                    gotoPage(0); // If input is empty, go to page 1 (index 0)
                } else {
                    const pageNum = Number(pageNumber);
                    if (pageNum >= 1 && pageNum <= pageCount) {
                        gotoPage(pageNum - 1); // Apply the page change on Enter
                    }
                }
            }
        }
    };

    // Handle when the input loses focus (clicks outside the input box)
    const handleBlur = (event) => {
        const pageNumber = event.target.value;
        if (manual) {
            const pageNum = Number(pageNumber);
            if (pageNum >= 1 && pageNum <= pages) {
                if (pageNum === pageNumberInManualPagination) return; // to avoid calling api if page number is not changed in manual pagination
                setPageNumberInManualPagination(pageNum);
                fetchData(state, pageNum - 1);
            }
        } else {
            if (pageNumber === "") {
                gotoPage(0); // If input is empty, go to page 1 (index 0)
            } else {
                const pageNum = Number(pageNumber);
                if (pageNum >= 1 && pageNum <= pageCount) {
                    gotoPage(pageNum - 1); // Apply the page change on blur
                }
            }
        }
    };

    const previousButtonHandler = () => {
        if (manual) {
            fetchData(state, inputPage - 2);
            setInputPage(inputPage - 1);
        } else {
            previousPage();
            setInputPage(pageIndex);
        }
    };

    const nextButtonHandler = () => {
        if (manual) {
            fetchData(state, inputPage);
            setInputPage(inputPage + 1);
        } else {
            nextPage();
            setInputPage(pageIndex + 2);
        }
    };

    const renderHeader = (column, index) => (
        <>
            {columnsReorderingEnabled && (
                <span
                    className="rt-order rt-order--left"
                    title="Move left"
                    onClick={() => moveColumnLeft(index)}
                >
                    <OutotecIcons type={CHEVRON_UP} />
                </span>
            )}
            <div>
                <div
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    title={column.Header}
                    className="rt-resizable-header-content"
                    style={{ position: "static", cursor: "pointer" }}
                >
                    {!column.Header.startsWith("Column") &&
                        column.render("Header")}
                    <div className="rt-sort" title="sort">
                        <div className="rt-sort-desc">
                            <OutotecIcons type={SORT_UP} />
                        </div>
                        <div className="rt-sort-asc">
                            <OutotecIcons type={SORT_DOWN} />
                        </div>
                    </div>
                </div>
            </div>
            {columnsReorderingEnabled && (
                <span
                    className="rt-order rt-order--right"
                    title="Move right"
                    onClick={() => moveColumnRight(index)}
                >
                    <OutotecIcons type={CHEVRON_UP} />
                </span>
            )}
        </>
    );

    return (
        <>
            <div {...getTableProps()} className="ReactTable">
                <div className="rt-table">
                    <div
                        className="rt-thead -header"
                        style={{ minWidth: totalColumnWidth }}
                    >
                        {headerGroups.map((headerGroup, index) => (
                            <div
                                {...headerGroup.getHeaderGroupProps()}
                                className="rt-tr"
                                key={index}
                            >
                                {headerGroup.headers.map((column, index) => {
                                    return (
                                        column.show !== false &&
                                        !column.Header.startsWith("Column ") &&
                                        !hideHeader && (
                                            <div
                                                key={column.id}
                                                className={classnames(
                                                    {
                                                        "rt-resizing":
                                                            column.isResizing,
                                                    },
                                                    column.isSorted
                                                        ? column.isSortedDesc
                                                            ? "-sort-desc"
                                                            : "-sort-asc"
                                                        : "",
                                                    "rt-th",
                                                    {
                                                        "-cursor-pointer":
                                                            isSortable,
                                                    },
                                                    {
                                                        "rt-columnEditable":
                                                            columnsReorderingEnabled,
                                                    }
                                                )}
                                                style={{
                                                    minWidth:
                                                        column.minWidth ||
                                                        column.width,
                                                    width: column.width,
                                                    flexGrow:
                                                        column.id !==
                                                            "expander" &&
                                                        column.id !==
                                                            "remover" &&
                                                        column.width,
                                                    flexShrink: 0,
                                                    flexBasis: "auto",
                                                    ...(column.fixed ===
                                                        "left" && {
                                                        position: "sticky",
                                                        left: column.totalLeft, // Fix the column to the left
                                                        backgroundColor:
                                                            "white", // Optional, to avoid overlap with other columns
                                                        zIndex: 1,
                                                    }),
                                                }}
                                            >
                                                {(isSortable &&
                                                    column.canSort) ||
                                                manual
                                                    ? renderHeader(
                                                          column,
                                                          index
                                                      )
                                                    : !column.Header.startsWith(
                                                          "Column"
                                                      )
                                                    ? column.render("Header")
                                                    : null}
                                                {column.canResize && (
                                                    <span
                                                        {...column.getResizerProps()}
                                                        className="rt-resizer"
                                                    />
                                                )}
                                            </div>
                                        )
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                    {filterable && (
                        <div
                            className="rt-thead -filters"
                            style={{ minWidth: totalColumnWidth }}
                        >
                            {headerGroups.map((headerGroup, index) => (
                                <div
                                    {...headerGroup.getHeaderGroupProps()}
                                    className="rt-tr"
                                    key={index}
                                >
                                    {headerGroup.headers.map((column) => {
                                        return (
                                            column.show !== false && (
                                                <div
                                                    className="rt-th"
                                                    key={column.id}
                                                    style={{
                                                        width: column.width,
                                                        minWidth:
                                                            column.minWidth ||
                                                            column.width,
                                                        flexGrow:
                                                            column.id !==
                                                                "expander" &&
                                                            column.id !==
                                                                "remover" &&
                                                            column.width,
                                                        flexShrink: 0,
                                                        flexBasis: "auto",
                                                        ...(column.fixed ===
                                                            "left" && {
                                                            position: "sticky",
                                                            left: column.totalLeft, // Fix the column to the left
                                                            backgroundColor:
                                                                "white", // Optional, to avoid overlap with other columns
                                                            zIndex: 1,
                                                        }),
                                                    }}
                                                >
                                                    {fromSearchDataGrid
                                                        ? !manual &&
                                                          filterable &&
                                                          (column.filterable ? (
                                                              column.render(
                                                                  "Filter"
                                                              )
                                                          ) : (
                                                              <span
                                                                  className="rt-filter-removed"
                                                                  title="Please use search filters if needed"
                                                              >
                                                                  Filter not
                                                                  available
                                                              </span>
                                                          ))
                                                        : !manual &&
                                                          filterable &&
                                                          column.canFilter &&
                                                          column.render(
                                                              "Filter"
                                                          )}
                                                    {manual && filterable && (
                                                        <input
                                                            type="text"
                                                            value={
                                                                column.filterValue ||
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                column.setFilter(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            onKeyDown={(
                                                                event
                                                            ) => {
                                                                if (
                                                                    event.key ===
                                                                    "Enter"
                                                                ) {
                                                                    const inputField =
                                                                        event.target as HTMLTextAreaElement;
                                                                    inputField.blur();
                                                                }
                                                            }}
                                                            onBlur={() =>
                                                                fetchData(
                                                                    state,
                                                                    inputPage -
                                                                        1
                                                                )
                                                            }
                                                            className="form-control"
                                                            placeholder={
                                                                "Filter..."
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            )
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                    <div
                        {...getTableBodyProps()}
                        className="rt-tbody"
                        style={{ minWidth: totalColumnWidth }}
                    >
                        {page.map((row) => {
                            prepareRow(row);
                            return (
                                <React.Fragment key={row.id}>
                                    <div
                                        {...row.getRowProps()}
                                        className="rt-tr"
                                    >
                                        {row.cells.map(
                                            (cell, index) =>
                                                cell.column.show !== false && (
                                                    <div
                                                        {...cell.getCellProps()}
                                                        key={cell.column.id}
                                                        className={classnames(
                                                            "rt-td ItemGroup__column",
                                                            lastFixedColumnIndex ===
                                                                index + 1 &&
                                                                "fixedColumnRightBorder",
                                                            {
                                                                "rt-expandable":
                                                                    cell.column
                                                                        .id ===
                                                                    "expander",
                                                            }
                                                        )}
                                                        style={{
                                                            minWidth:
                                                                cell.column
                                                                    .minWidth ||
                                                                cell.column
                                                                    .width,
                                                            width: cell.column
                                                                .width,
                                                            flexGrow:
                                                                cell.column
                                                                    .width,
                                                            flexShrink: 0,
                                                            flexBasis: "auto",
                                                            transition:
                                                                cell.column
                                                                    .isResizing &&
                                                                "none",
                                                            ...(cell.column
                                                                .fixed ===
                                                                "left" && {
                                                                position:
                                                                    "sticky",
                                                                left: cell
                                                                    .column
                                                                    .totalLeft, // Fix the column to the left
                                                                backgroundColor:
                                                                    "white", // Optional, to avoid overlap with other columns
                                                                zIndex: 1,
                                                            }),
                                                        }}
                                                    >
                                                        {cell.render("Cell")}
                                                    </div>
                                                )
                                        )}
                                    </div>
                                    {row.isExpanded && SubComponent(row)}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
                {page.length === 0 && (
                    <div className="rt-noData">No rows found</div>
                )}
                <div className={loading ? "-loading -active" : ""}>
                    {loading && <div>Loading...</div>}
                </div>
            </div>
            {/* Pagination Controls */}
            {showPagination && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "stretch",
                        marginTop: "10px",
                        justifyContent: "space-between",
                    }}
                >
                    <div style={{ width: "25%" }}>
                        <button
                            className="paginationButton"
                            onClick={previousButtonHandler}
                            disabled={
                                manual ? inputPage <= 1 : !canPreviousPage
                            }
                            style={{ fontSize: "0.8rem" }}
                        >
                            Previous
                        </button>
                    </div>
                    {/* Page Info */}
                    <div style={{ textAlign: "center" }}>
                        <span style={{ color: "#009CD9", fontSize: "0.8rem" }}>
                            Page
                            <input
                                type="number"
                                value={inputPage} // Page index is 0-based, so display it as 1-based
                                min={1}
                                max={manual ? pages : pageCount}
                                onChange={handlePageInput}
                                onKeyDown={handleKeyPress}
                                onBlur={handleBlur}
                                style={{
                                    width: "50px",
                                    textAlign: "center",
                                    margin: "0 5px",
                                    border: " 1px solid lightgrey",
                                    minHeight: 30,
                                    minWidth: 70,
                                    outline: "none",
                                }}
                            />
                            of {manual ? pages : pageCount ?? 1}
                        </span>
                        {/* Rows per page Dropdown */}
                        <div
                            style={{
                                display: "inline-block",
                                minWidth: 100,
                                fontSize: "0.8rem",
                                marginLeft: 10,
                            }}
                        >
                            <Select
                                isSearchable={false}
                                value={numberOfRowsOptions.find(
                                    (option) => option.value === pageSize
                                )}
                                onChange={(selectedOption) =>
                                    setPageSize(selectedOption.value)
                                }
                                options={numberOfRowsOptions}
                                components={{
                                    DropdownIndicator:
                                        CustomDropdownIndicatorInPagination,
                                }}
                                menuPlacement="top"
                                styles={{
                                    dropdownIndicator: (base) => ({
                                        ...base,
                                        padding: 8,
                                        svg: {
                                            fill: "#009CD9",
                                            width: 9,
                                            height: 9,
                                        },
                                        cursor: "pointer",
                                    }),
                                    control: (styles, { isFocused }) => ({
                                        ...styles,
                                        borderColor: isFocused
                                            ? "lightgrey"
                                            : "lightgrey", // Remove blue border on focus
                                        boxShadow: "none", // Remove any box shadow (blue highlight)
                                        ":hover": {
                                            borderColor: "lightgrey",
                                        },
                                        minHeight: 30,
                                    }),
                                    option: (provided, state) => ({
                                        ...provided,
                                        fontSize: "0.8rem",
                                        padding: "2px 8px", // Reducing the padding for smaller space between options
                                        backgroundColor: state.isFocused
                                            ? "grey" // Set background color to lightgrey when hovered
                                            : "white", // Default background color
                                        color: state.isFocused
                                            ? "white"
                                            : "black",
                                    }),
                                    indicatorSeparator: (provided) => ({
                                        ...provided,
                                        display: "none", // Hides the indicator separator
                                    }),
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ width: "25%" }}>
                        <button
                            className="paginationButton"
                            onClick={nextButtonHandler}
                            disabled={
                                manual ? inputPage >= pages : !canNextPage
                            }
                            style={{ fontSize: "0.8rem" }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};
